import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { getErrorMessage } from '@/lib/errorMessage';
import { redirectToDonationCheckout } from '@/lib/analytics';

const QUICK_AMOUNTS = [5, 10, 25];

export const DonateButton = () => {
  const [amount, setAmount] = useState<number | ''>('');
  const [selectedQuick, setSelectedQuick] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveAmount = selectedQuick ?? (typeof amount === 'number' ? amount : 0);

  const handleQuickPick = (value: number) => {
    setSelectedQuick(value);
    setAmount('');
    setError(null);
  };

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedQuick(null);
    setError(null);
    const raw = e.target.value;
    if (raw === '') {
      setAmount('');
      return;
    }
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) {
      setAmount(parsed);
    }
  };

  const handleDonate = async () => {
    if (effectiveAmount < 3) {
      setError('Minimum donation is $3');
      return;
    }
    if (effectiveAmount > 500) {
      setError('Maximum donation is $500');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('create-donation-checkout', {
        body: { amount: effectiveAmount },
      });

      if (fnError) throw fnError;
      if (data?.url) {
        redirectToDonationCheckout(data.url, effectiveAmount, data.sessionId, data.nonce);
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      setError(getErrorMessage(err) || 'Something went wrong');
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {QUICK_AMOUNTS.map((val) => (
        <button
          key={val}
          onClick={() => handleQuickPick(val)}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors border ${
            selectedQuick === val
              ? 'bg-gold text-slate-950 border-gold'
              : 'bg-secondary/50 text-foreground/70 border-border hover:border-gold/50'
          }`}
        >
          ${val}
        </button>
      ))}

      <div className="relative">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
        <input
          type="number"
          min={3}
          max={500}
          step="1"
          placeholder="Other"
          value={selectedQuick ? '' : amount}
          onChange={handleCustomChange}
          onFocus={() => setSelectedQuick(null)}
          className="w-20 pl-6 pr-2 py-1.5 rounded-md text-sm bg-secondary/50 border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-gold/50"
        />
      </div>

      <Button
        onClick={handleDonate}
        disabled={loading || effectiveAmount < 3}
        size="sm"
        className="bg-gold hover:bg-gold-light text-slate-950 font-semibold"
      >
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
        {effectiveAmount >= 3 ? `Donate $${effectiveAmount}` : 'Donate'}
      </Button>

      {error && <p className="text-xs text-red-400 w-full">{error}</p>}
    </div>
  );
};
